<template>
  <div
    class="vue-map-container"
    :class="$attrs.class"
    ref="vueMapObserverTarget"
  >
    <div v-if="!mapVisible" class="vue-map-placeholder">
      <img :src="mapPlaceholderImage" alt="Map placeholder" />
    </div>

    <div
      v-show="mapVisible"
      class="vue-map"
      ref="vue-map"
      :style="$attrs.style || ''"
      :id="mapId"
    ></div>

    <div class="vue-map-hidden">
      <slot></slot>
    </div>
    <slot name="visible"></slot>
  </div>
</template>

<script>
import bindEvents from "../utils/bindEvents.js";
import { bindProps, getPropsValues } from "../utils/bindProps.js";
import mountableMixin from "../utils/mountableMixin.js";
import TwoWayBindingWrapper from "../utils/TwoWayBindingWrapper.js";
import WatchPrimitiveProperties from "../utils/WatchPrimitiveProperties.js";
import { mappedPropsToVueProps } from "./build-component.js";

const googleMapProps = {
  center: { required: true, twoWay: true, type: Object, noBind: true },
  zoom: { required: false, twoWay: true, type: Number, noBind: true },
  heading: { type: Number, twoWay: true },
  mapTypeId: { twoWay: true, type: String },
  tilt: { twoWay: true, type: Number },
  options: { type: Object, default: () => ({}) },
};

const localProps = {
  mapPlaceholderImage: { type: String, required: true },
};

const props = {
  ...googleMapProps,
  ...localProps,
};

const events = [
  "bounds_changed",
  "click",
  "dblclick",
  "drag",
  "dragend",
  "dragstart",
  "idle",
  "mousemove",
  "mouseout",
  "mouseover",
  "resize",
  "rightclick",
  "tilesloaded",
];

const linkedMethods = ["panBy", "panTo", "panToBounds", "fitBounds"].reduce(
  (all, methodName) => {
    all[methodName] = function () {
      if (this.$mapObject) {
        this.$mapObject[methodName].apply(this.$mapObject, arguments);
      }
    };
    return all;
  },
  {}
);

const customMethods = {
  resize() {
    if (this.$mapObject) {
      google.maps.event.trigger(this.$mapObject, "resize");
    }
  },
  resizePreserveCenter() {
    if (!this.$mapObject) return;
    const oldCenter = this.$mapObject.getCenter();
    google.maps.event.trigger(this.$mapObject, "resize");
    this.$mapObject.setCenter(oldCenter);
  },
  _resizeCallback() {
    this.resizePreserveCenter();
  },
};

export default {
  mixins: [mountableMixin],
  props: mappedPropsToVueProps({
    ...props,
    ...events.reduce(
      (obj, eventName) => ({
        ...obj,
        [`on${eventName.charAt(0).toUpperCase()}${eventName.slice(1)}`.replace(
          /[-_]+(.)?/g,
          (_, c) => (c ? c.toUpperCase() : "")
        )]: Function,
      }),
      {}
    ),
  }),
  inheritAttrs: false,
  data() {
    return {
      mapId: `vue-map-${this._uid}`,
      observer: null,
      mapVisible: false,
      mapLoaded: false,
    };
  },
  provide() {
    this.$mapPromise = new Promise((resolve, reject) => {
      this.$mapPromiseDeferred = { resolve, reject };
    });
    return {
      $mapPromise: this.$mapPromise,
    };
  },
  emits: ["center_changed", "zoom_changed", "bounds_changed", "mapResolved"],
  computed: {
    finalLat() {
      return typeof this.center.lat === "function"
        ? this.center.lat()
        : this.center.lat;
    },
    finalLng() {
      return typeof this.center.lng === "function"
        ? this.center.lng()
        : this.center.lng;
    },
    finalLatLng() {
      return { lat: this.finalLat, lng: this.finalLng };
    },
  },
  watch: {
    zoom(zoom) {
      if (this.$mapObject) {
        this.$mapObject.setZoom(zoom);
      }
    },
  },

  mounted() {
    if ("IntersectionObserver" in window) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            this.mapVisible = true;
            this.initMap();
            this.observer.disconnect();
          }
        },
        {
          rootMargin: "0px",
          threshold: 0.1,
        }
      );
      this.observer.observe(this.$refs.vueMapObserverTarget);
    } else {
      this.mapVisible = true;
      this.initMap();
    }
  },

  methods: {
    ...customMethods,
    ...linkedMethods,
    initMap() {
      if (this.mapLoaded) return;

      this.$gmapApiLoader
        .importLibrary("maps")
        .then((maps) => {
          const element = this.$refs["vue-map"];
          const options = {
            ...this.options,
            ...getPropsValues(this, googleMapProps),
            mapId: element.id,
          };
          delete options.options;

          this.$mapObject = new maps.Map(element, options);
          bindProps(this, this.$mapObject, googleMapProps);
          bindEvents(this, this.$mapObject, events);

          this.initMapListeners();

          this.$mapPromiseDeferred.resolve(this.$mapObject);
          this.$emit("mapResolved", this.$mapObject);
          this.mapLoaded = true;
        })
        .catch((error) => {
          console.error("Google Maps failed to load:", error);
        });
    },

    initMapListeners() {
      TwoWayBindingWrapper((increment, decrement, shouldUpdate) => {
        this.$mapObject.addListener("center_changed", () => {
          if (shouldUpdate()) {
            this.$emit("center_changed", this.$mapObject.getCenter());
          }
          decrement();
        });

        const updateCenter = () => {
          increment();
          this.$mapObject.setCenter(this.finalLatLng);
        };

        WatchPrimitiveProperties(this, ["finalLat", "finalLng"], updateCenter);
      });

      this.$mapObject.addListener("zoom_changed", () => {
        this.$emit("zoom_changed", this.$mapObject.getZoom());
      });

      this.$mapObject.addListener("bounds_changed", () => {
        this.$emit("bounds_changed", this.$mapObject.getBounds());
      });
    },
  },
};
</script>

<style>
.vue-map {
  width: 100%;
  height: 100%;
  min-height: 200px;
}
.vue-map-placeholder {
  width: 100%;
  height: 100%;
}
.vue-map-placeholder img {
  width: 100%;
  height: auto;
  display: block;
}
</style>
